// src/pages/relatorios/MovimentosPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Form, InputGroup, Row, Col, Spinner, Alert, Pagination, Badge } from 'react-bootstrap';
import { FaSearch, FaFilter, FaPrint } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import api from '../../service/api';

interface Movimento {
  MOV_CODIGO: number;
  LIG_CODIGO: number;
  MOV_ANO: string;
  MOV_MES: string;
  MOV_CONSUMO: number;
  MOV_VALOR: number;
  MOV_SITUACAO: string;
  MOV_DATA: string | null;
  MOV_DATAPGTO: string | null;
  MOV_TOTALPAGO: number;
  MOV_ACRESCIMOS: number;
  PES_NOME?: string;
  LIG_ENDERECO?: string;
  LIG_FICHA?: number;
}

interface Pessoa {
  PES_CODIGO: number;
  PES_NOME: string;
  PES_ENDERECO?: string;
}

interface Ligacao {
  LIG_CODIGO: number;
  LIG_ENDERECO: string;
  LIG_FICHA: number;
  PES_CODIGO: number;
}

const ITEMS_PER_PAGE = 15;

const NOMES_MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const nomeMes = (m: string) => NOMES_MESES[parseInt(m, 10) - 1] || m;

const getSituacaoBadge = (s: string) => {
  if (s === 'A') return <Badge bg="warning">Aberto</Badge>;
  if (s === 'P') return <Badge bg="success">Pago</Badge>;
  if (s === 'C') return <Badge bg="danger">Cancelado</Badge>;
  return <Badge bg="secondary">{s}</Badge>;
};

const getSituacaoText = (s: string) =>
  s === 'A' ? 'Em Aberto' : s === 'P' ? 'Pago' : s === 'C' ? 'Cancelado' : s;

const thStyle: React.CSSProperties = {
  border: '1px solid #dee2e6',
  padding: '4px 6px',
  textAlign: 'left',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  backgroundColor: '#e9ecef',
};

const tdStyle: React.CSSProperties = {
  border: '1px solid #dee2e6',
  padding: '3px 6px',
  whiteSpace: 'nowrap',
};

const RelMovimentosPage: React.FC = () => {
  const hoje = new Date();
  const anoAtual = hoje.getFullYear().toString();
  const mesAnterior = String(hoje.getMonth() === 0 ? 12 : hoje.getMonth()).padStart(2, '0');
  const anoAnterior = hoje.getMonth() === 0 ? (hoje.getFullYear() - 1).toString() : anoAtual;

  const [movimentos, setMovimentos] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSituacao, setFilterSituacao] = useState('');
  const [filterAno, setFilterAno] = useState(anoAnterior);
  const [filterMes, setFilterMes] = useState(mesAnterior);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredMovCode, setHoveredMovCode] = useState<number | null>(null);

  const fetchMovimentos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [movRes, ligRes, pesRes] = await Promise.all([
        api.get('/movimentos'),
        api.get('/ligacoes'),
        api.get('/pessoas'),
      ]);

      const ligacaoMap = new Map<number, { endereco: string; ficha: number; pes_codigo: number }>(
        ligRes.data.map((l: Ligacao) => [l.LIG_CODIGO, { endereco: l.LIG_ENDERECO, ficha: l.LIG_FICHA, pes_codigo: l.PES_CODIGO }])
      );
      const pessoasMap = new Map<number, { nome: string; endereco: string }>(
        pesRes.data.map((p: Pessoa) => [p.PES_CODIGO, { nome: p.PES_NOME, endereco: p.PES_ENDERECO ?? '' }])
      );

      const enriched = movRes.data.map((m: Movimento) => {
        const lig = ligacaoMap.get(m.LIG_CODIGO);
        const pes = lig ? pessoasMap.get(lig.pes_codigo) : undefined;
        const endereco = lig?.endereco || pes?.endereco || '';
        return {
          ...m,
          PES_NOME: pes?.nome ?? '',
          LIG_ENDERECO: endereco,
          LIG_FICHA: lig?.ficha ?? 0,
        };
      }).sort((a: Movimento, b: Movimento) =>
        (a.PES_NOME || '').localeCompare(b.PES_NOME || '', 'pt-BR')
      );

      setMovimentos(enriched);
    } catch {
      setError('Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMovimentos(); }, [fetchMovimentos]);

  const filtered = movimentos.filter(m => {
    const searchLower = searchTerm.toLocaleLowerCase();
    const matchSearch =
      String(m.LIG_FICHA).includes(searchTerm) ||
      (m.PES_NOME || '').toLocaleLowerCase().includes(searchLower) ||
      (m.LIG_ENDERECO || '').toLocaleLowerCase().includes(searchLower);

    return matchSearch &&
      (filterSituacao === '' ||
        (filterSituacao === 'pago'   && (m.MOV_SITUACAO === 'P' || !!m.MOV_DATAPGTO)) ||
        (filterSituacao === 'aberto' && m.MOV_SITUACAO !== 'P' && !m.MOV_DATAPGTO && m.MOV_SITUACAO !== 'C')
      ) &&
      (filterAno === '' || m.MOV_ANO === filterAno) &&
      (filterMes === '' || m.MOV_MES === filterMes);
  });

  const { sortedData: sortedMovimentos, sortKey, sortDir, handleSort } = useSortable<Movimento>(filtered, 'MOV_ANO', 'desc');
  const paginated = sortedMovimentos.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const isPago   = (m: Movimento) => m.MOV_SITUACAO === 'P' || !!m.MOV_DATAPGTO;
  const isAberto = (m: Movimento) => m.MOV_SITUACAO === 'A' && !m.MOV_DATAPGTO;

  const totalValor  = filtered.reduce((acc, m) => acc + Number(m.MOV_VALOR || 0), 0);
  const totalPago   = filtered.filter(isPago).reduce((acc, m) => acc + Number(m.MOV_TOTALPAGO || m.MOV_VALOR || 0), 0);
  const totalAberto = filtered.filter(isAberto).reduce((acc, m) => acc + Number(m.MOV_VALOR || 0), 0);

  const anos  = Array.from(new Set(movimentos.map(m => m.MOV_ANO))).sort().reverse();
  const meses = ['01','02','03','04','05','06','07','08','09','10','11','12'];

  const descFiltros = [
    filterAno    ? `Ano: ${filterAno}`                       : 'Todos os anos',
    filterMes    ? `Mês: ${nomeMes(filterMes)}`              : 'Todos os meses',
    filterSituacao === 'pago' ? 'Situação: Pago' : filterSituacao === 'aberto' ? 'Situação: Em Aberto' : 'Todas as situações',
    searchTerm   ? `Busca: "${searchTerm}"`                  : null,
  ].filter(Boolean).join('  |  ');

  return (
    <>
      <style>{`
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .navbar, header, nav, footer { display: none !important; }
          .container, .container-fluid, .container-sm, .container-md, .container-lg, .container-xl, .container-xxl {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; color: #000; }
          @page { size: landscape; margin: 0.8cm 0.5cm; }
        }
        .mov-row-hovered td { background-color: #fff3cd !important; cursor: pointer; }
        .rel-table { font-size: 14px; }
        .rel-table th, .rel-table td { padding: 6px 8px; }
      `}</style>

      {/* ── Cabeçalho de tela ── */}
      <div className="no-print d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Relatório de Movimentos</h1>
        <Button variant="outline-secondary" onClick={() => window.print()}>
          {FaPrint({ className: 'me-2' })} Imprimir
        </Button>
      </div>

      {error && <Alert variant="danger" className="no-print">{error}</Alert>}

      {/* ── Filtros ── */}
      <Card className="shadow-sm mb-3 no-print">
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por Ficha, Nome, Endereço..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select value={filterAno} onChange={e => { setFilterAno(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos os anos</option>
                {anos.map(a => <option key={a} value={a}>{a}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select value={filterMes} onChange={e => { setFilterMes(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos os meses</option>
                {meses.map(m => <option key={m} value={m}>{nomeMes(m)} ({m})</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <InputGroup>
                <InputGroup.Text>{FaFilter({})}</InputGroup.Text>
                <Form.Select value={filterSituacao} onChange={e => { setFilterSituacao(e.target.value); setCurrentPage(1); }}>
                  <option value="">Todos</option>
                  <option value="aberto">Em Aberto</option>
                  <option value="pago">Pago</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted small">
                {filtered.length} registros &nbsp;|&nbsp;
                Total: {formatCurrency(totalValor)} &nbsp;|&nbsp;
                Recebido: {formatCurrency(totalPago)}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Tabela paginada (tela) ── */}
      <Card className="shadow-sm no-print">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhum movimento encontrado.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm" className="rel-table">
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Movimento> label="Código" sortKey="MOV_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Ficha" sortKey="LIG_FICHA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Mês/Ano" sortKey="MOV_ANO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Consumo" sortKey="MOV_CONSUMO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Valor" sortKey="MOV_VALOR" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Dt. Emissão" sortKey="MOV_DATA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Dt. Pagto" sortKey="MOV_DATAPGTO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Total Pago" sortKey="MOV_TOTALPAGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Situação" sortKey="MOV_SITUACAO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(m => (
                      <React.Fragment key={m.MOV_CODIGO}>
                        <tr
                          className={`table-secondary ${hoveredMovCode === m.MOV_CODIGO ? 'mov-row-hovered' : ''}`}
                          onMouseEnter={() => setHoveredMovCode(m.MOV_CODIGO)}
                          onMouseLeave={() => setHoveredMovCode(null)}
                        >
                          <td colSpan={9}>
                            <small><strong>👤 {m.PES_NOME}</strong> • 📍 {m.LIG_ENDERECO}</small>
                          </td>
                        </tr>
                        <tr
                          className={hoveredMovCode === m.MOV_CODIGO ? 'mov-row-hovered' : ''}
                          onMouseEnter={() => setHoveredMovCode(m.MOV_CODIGO)}
                          onMouseLeave={() => setHoveredMovCode(null)}
                        >
                          <td>{m.MOV_CODIGO}</td>
                          <td>{m.LIG_FICHA}</td>
                          <td>{m.MOV_MES}/{m.MOV_ANO}</td>
                          <td>{m.MOV_CONSUMO} m³</td>
                          <td>{formatCurrency(m.MOV_VALOR)}</td>
                          <td>{formatDate(m.MOV_DATA)}</td>
                          <td>{formatDate(m.MOV_DATAPGTO)}</td>
                          <td>{formatCurrency(m.MOV_TOTALPAGO)}</td>
                          <td>{getSituacaoBadge(m.MOV_SITUACAO)}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Versão de impressão (todos os registros filtrados) ── */}
      <div className="print-only">
        {/* Cabeçalho */}
        <div style={{ borderBottom: '2px solid #333', paddingBottom: '8px', marginBottom: '12px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center' }}>
            Sociedade Amigos de Coqueiros — Relatório de Movimentos
          </div>
          <div style={{ fontSize: '11px', textAlign: 'center', color: '#444', marginTop: '4px' }}>
            {descFiltros}
          </div>
          <div style={{ fontSize: '10px', textAlign: 'right', color: '#666', marginTop: '2px' }}>
            Emitido em: {hoje.toLocaleDateString('pt-BR')} às {hoje.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Tabela completa */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Cód.</th>
              <th style={thStyle}>Ficha</th>
              <th style={thStyle}>Nome</th>
              <th style={thStyle}>Endereço</th>
              <th style={thStyle}>Mês/Ano</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Consumo</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Valor</th>
              <th style={thStyle}>Dt. Emissão</th>
              <th style={thStyle}>Dt. Pagto</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Total Pago</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Situação</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, idx) => (
              <tr key={m.MOV_CODIGO} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                <td style={tdStyle}>{m.MOV_CODIGO}</td>
                <td style={tdStyle}>{m.LIG_FICHA}</td>
                <td style={tdStyle}>{m.PES_NOME}</td>
                <td style={tdStyle}>{m.LIG_ENDERECO}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{m.MOV_MES}/{m.MOV_ANO}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{m.MOV_CONSUMO} m³</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.MOV_VALOR)}</td>
                <td style={tdStyle}>{formatDate(m.MOV_DATA)}</td>
                <td style={tdStyle}>{formatDate(m.MOV_DATAPGTO)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.MOV_TOTALPAGO)}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{getSituacaoText(m.MOV_SITUACAO)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Rodapé com totais */}
        <div style={{ borderTop: '2px solid #333', marginTop: '12px', paddingTop: '8px', fontSize: '11px' }}>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td><strong>Total de registros:</strong> {filtered.length}</td>
                <td style={{ textAlign: 'center' }}><strong>Total lançado:</strong> {formatCurrency(totalValor)}</td>
                <td style={{ textAlign: 'center' }}><strong>Total recebido:</strong> {formatCurrency(totalPago)}</td>
                <td style={{ textAlign: 'right' }}><strong>Total em aberto:</strong> {formatCurrency(totalAberto)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default RelMovimentosPage;
